'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { ChevronsUpDown, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './Popover2';
import Button from './Button';
import { Badge } from '~/components/ui/badge';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from './Command';
import { Checkbox } from '~/components/ui/checkbox';
import { cn } from '~/utils';
import Icon, { type IconName } from './Icon';
export interface MultiSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}
export interface MultiSelectComboBoxProps {
  values: string[];
  options: MultiSelectOption[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  popoverClassName?: string;
  icon?: IconName;
  isLoading?: boolean;
  hasError?: boolean;
  onSearch?: (value: string) => void;
  maxDisplay?: number; // Max number of selected items to show as badges
  showClearButton?: boolean;
  disabled?: boolean;
  side?: React.ComponentProps<typeof PopoverContent>['side'];
  avoidCollisions?: boolean;
}
export default function MultiSelectComboBox(t0) {
  const $ = _c(96);
  const {
    values,
    options,
    onChange,
    placeholder: t1,
    searchPlaceholder: t2,
    emptyMessage: t3,
    className,
    popoverClassName,
    icon,
    isLoading,
    hasError,
    onSearch,
    maxDisplay: t4,
    showClearButton: t5,
    disabled: t6,
    side,
    avoidCollisions: t7
  } = t0;
  const placeholder = t1 === undefined ? "Select options..." : t1;
  const searchPlaceholder = t2 === undefined ? "Search options..." : t2;
  const emptyMessage = t3 === undefined ? "No options found." : t3;
  const maxDisplay = t4 === undefined ? 2 : t4;
  const showClearButton = t5 === undefined ? true : t5;
  const disabled = t6 === undefined ? false : t6;
  const avoidCollisions = t7 === undefined ? true : t7;
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");
  let t8;
  bb0: {
    if (!searchValue.trim()) {
      t8 = options;
      break bb0;
    }
    let t9;
    if ($[0] !== options || $[1] !== searchValue) {
      let t10;
      if ($[3] !== searchValue) {
        t10 = option => option.label.toLowerCase().includes(searchValue.toLowerCase());
        $[3] = searchValue;
        $[4] = t10;
      } else {
        t10 = $[4];
      }
      t9 = options.filter(t10);
      $[0] = options;
      $[1] = searchValue;
      $[2] = t9;
    } else {
      t9 = $[2];
    }
    t8 = t9;
  }
  const filteredOptions = t8;
  let t9;
  if ($[5] !== values) {
    t9 = new Set(values);
    $[5] = values;
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  const selectedSet = t9;
  let t10;
  if ($[7] !== options || $[8] !== selectedSet) {
    let t11;
    if ($[10] !== selectedSet) {
      t11 = option_0 => selectedSet.has(option_0.value);
      $[10] = selectedSet;
      $[11] = t11;
    } else {
      t11 = $[11];
    }
    t10 = options.filter(t11);
    $[7] = options;
    $[8] = selectedSet;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  const selectedOptions = t10;
  let t11;
  if ($[12] !== values) {
    t11 = new Set(values);
    $[12] = values;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  const selectedValuesSet = t11;
  let t12;
  if ($[14] !== onChange || $[15] !== values) {
    t12 = optionValue => {
      const newValues = values.includes(optionValue) ? values.filter(v => v !== optionValue) : [...values, optionValue];
      onChange(newValues);
    };
    $[14] = onChange;
    $[15] = values;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  const handleToggle = t12;
  let t13;
  if ($[17] !== onChange) {
    t13 = () => {
      onChange([]);
    };
    $[17] = onChange;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const handleClear = t13;
  let t14;
  if ($[19] !== onSearch) {
    t14 = value => {
      setSearchValue(value);
      onSearch?.(value);
    };
    $[19] = onSearch;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  const handleSearch = t14;
  let t15;
  if ($[21] !== handleToggle || $[22] !== maxDisplay || $[23] !== placeholder || $[24] !== selectedOptions || $[25] !== values.length) {
    t15 = () => {
      if (values.length === 0) {
        return <span className="text-blue-400">{placeholder}</span>;
      }
      if (values.length <= maxDisplay) {
        return <div className="flex flex-wrap gap-1">{selectedOptions.map(option_1 => <Badge key={option_1.value} variant="secondary" className="border-secondary/30 bg-secondary/20 px-2 py-0.5 text-xs text-secondary">{option_1.label}<span role="button" tabIndex={0} className="ml-1 cursor-pointer rounded-full p-0.5 hover:bg-secondary/30 focus:outline-none focus:ring-1 focus:ring-ring" onClick={e => {
              e.stopPropagation();
              handleToggle(option_1.value);
            }} onKeyDown={e_0 => {
              if (e_0.key === "Enter" || e_0.key === " ") {
                e_0.preventDefault();
                e_0.stopPropagation();
                handleToggle(option_1.value);
              }
            }} aria-label={`Remove ${option_1.label}`}><X className="h-3 w-3" /></span></Badge>)}</div>;
      }
      return <span className="text-primary">{values.length} selected</span>;
    };
    $[21] = handleToggle;
    $[22] = maxDisplay;
    $[23] = placeholder;
    $[24] = selectedOptions;
    $[25] = values.length;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  const getTriggerDisplay = t15;
  let t16;
  if ($[27] !== handleSearch) {
    t16 = newOpen => {
      setOpen(newOpen);
      if (!newOpen) {
        handleSearch("");
      }
    };
    $[27] = handleSearch;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  const t17 = hasError && "ring-1 ring-orange-700";
  const t18 = disabled && "cursor-not-allowed opacity-50";
  let t19;
  if ($[29] !== className || $[30] !== t17 || $[31] !== t18) {
    t19 = cn("min-h-12 justify-start rounded-4xl bg-white py-5 font-normal text-primary shadow-none", t17, t18, className);
    $[29] = className;
    $[30] = t17;
    $[31] = t18;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== icon) {
    t20 = icon && <Icon name={icon} className="mr-2 shrink-0 text-secondary" />;
    $[33] = icon;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== getTriggerDisplay) {
    t21 = <div className="flex-1 overflow-hidden text-left">{getTriggerDisplay()}</div>;
    $[35] = getTriggerDisplay;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] !== handleClear || $[38] !== values.length) {
    t22 = values.length > 0 && <span role="button" tabIndex={0} className="mr-2 cursor-pointer rounded-sm p-0.5 opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" onClick={e_1 => {
      e_1.stopPropagation();
      handleClear();
    }} onKeyDown={e_2 => {
      if (e_2.key === "Enter" || e_2.key === " ") {
        e_2.preventDefault();
        e_2.stopPropagation();
        handleClear();
      }
    }} aria-label="Clear all selections"><X className="h-4 w-4" /></span>;
    $[37] = handleClear;
    $[38] = values.length;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== disabled || $[42] !== open || $[43] !== t19 || $[44] !== t20 || $[45] !== t21 || $[46] !== t22) {
    t24 = <PopoverTrigger asChild={true}><Button variant="outline" aria-expanded={open} disabled={disabled} className={t19}>{t20}{t21}{t22}{t23}</Button></PopoverTrigger>;
    $[41] = disabled;
    $[42] = open;
    $[43] = t19;
    $[44] = t20;
    $[45] = t21;
    $[46] = t22;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] !== popoverClassName) {
    t25 = cn("w-full p-0", popoverClassName);
    $[48] = popoverClassName;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] !== handleSearch || $[51] !== isLoading || $[52] !== searchPlaceholder || $[53] !== searchValue) {
    t26 = <CommandInput isLoading={isLoading} value={searchValue} onValueChange={handleSearch} className="outline-hidden appearance-none border-0 bg-transparent p-0 py-4 pr-8 text-primary placeholder:text-blue-400 focus:ring-0" placeholder={searchPlaceholder} />;
    $[50] = handleSearch;
    $[51] = isLoading;
    $[52] = searchPlaceholder;
    $[53] = searchValue;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  if ($[55] !== handleSearch || $[56] !== searchValue) {
    t27 = searchValue && <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 rounded-sm p-0.5 opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" onClick={() => handleSearch("")} aria-label="Clear search"><X className="h-4 w-4" /></button>;
    $[55] = handleSearch;
    $[56] = searchValue;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] !== t26 || $[59] !== t27) {
    t28 = <div className="relative">{t26}{t27}</div>;
    $[58] = t26;
    $[59] = t27;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] !== emptyMessage) {
    t29 = <CommandEmpty>{emptyMessage}</CommandEmpty>;
    $[61] = emptyMessage;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  let t30;
  if ($[63] !== disabled || $[64] !== filteredOptions || $[65] !== handleToggle || $[66] !== selectedValuesSet) {
    let t31;
    if ($[68] !== disabled || $[69] !== handleToggle || $[70] !== selectedValuesSet) {
      t31 = option_2 => {
        const isSelected = selectedValuesSet.has(option_2.value);
        const isDisabled = option_2.disabled || disabled;
        return <CommandItem key={option_2.value} value={option_2.value} onSelect={() => !isDisabled && handleToggle(option_2.value)} disabled={isDisabled} className="flex items-center gap-2 px-2 py-2"><Checkbox checked={isSelected} disabled={isDisabled} className="pointer-events-none shrink-0" aria-hidden="true" /><span className="flex-1 truncate">{option_2.label}</span></CommandItem>;
      };
      $[68] = disabled;
      $[69] = handleToggle;
      $[70] = selectedValuesSet;
      $[71] = t31;
    } else {
      t31 = $[71];
    }
    t30 = filteredOptions.map(t31);
    $[63] = disabled;
    $[64] = filteredOptions;
    $[65] = handleToggle;
    $[66] = selectedValuesSet;
    $[67] = t30;
  } else {
    t30 = $[67];
  }
  let t31;
  if ($[72] !== t30) {
    t31 = <CommandGroup>{t30}</CommandGroup>;
    $[72] = t30;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  let t32;
  if ($[74] !== handleClear || $[75] !== handleSearch || $[76] !== showClearButton || $[77] !== values.length) {
    t32 = values.length > 0 && showClearButton && <><CommandSeparator /><CommandGroup><CommandItem onSelect={() => {
          handleClear();
          handleSearch("");
        }} className="justify-center text-center text-orange-500">Clear all ({values.length})</CommandItem></CommandGroup></>;
    $[74] = handleClear;
    $[75] = handleSearch;
    $[76] = showClearButton;
    $[77] = values.length;
    $[78] = t32;
  } else {
    t32 = $[78];
  }
  let t33;
  if ($[79] !== t31 || $[80] !== t32) {
    t33 = <CommandList className="max-h-[200px]">{t31}{t32}</CommandList>;
    $[79] = t31;
    $[80] = t32;
    $[81] = t33;
  } else {
    t33 = $[81];
  }
  let t34;
  if ($[82] !== t28 || $[83] !== t29 || $[84] !== t33) {
    t34 = <Command shouldFilter={false} className="overflow-hidden rounded-4xl">{t28}{t29}{t33}</Command>;
    $[82] = t28;
    $[83] = t29;
    $[84] = t33;
    $[85] = t34;
  } else {
    t34 = $[85];
  }
  let t35;
  if ($[86] !== avoidCollisions || $[87] !== side || $[88] !== t25 || $[89] !== t34) {
    t35 = <PopoverContent className={t25} side={side} align="start" avoidCollisions={avoidCollisions}>{t34}</PopoverContent>;
    $[86] = avoidCollisions;
    $[87] = side;
    $[88] = t25;
    $[89] = t34;
    $[90] = t35;
  } else {
    t35 = $[90];
  }
  let t36;
  if ($[91] !== open || $[92] !== t16 || $[93] !== t24 || $[94] !== t35) {
    t36 = <Popover open={open} onOpenChange={t16}>{t24}{t35}</Popover>;
    $[91] = open;
    $[92] = t16;
    $[93] = t24;
    $[94] = t35;
    $[95] = t36;
  } else {
    t36 = $[95];
  }
  return t36;
}